import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ReviewsCarousel from "@/components/ReviewsCarousel";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import heroImage from "@/assets/hero-bo-barbers-team.jpg";
import depotProducts from "@/assets/depot-products.jpg";
import shopInterior from "@/assets/shop-interior.jpg";
import gallery1 from "@/assets/gallery-1.webp";
import aboutTeamImage from "@/assets/about-team.jpg";
import gallery2 from "@/assets/gallery-2.webp";
import gallery3 from "@/assets/gallery-3.webp";
import gallery4 from "@/assets/gallery-4.webp";
import gallery5 from "@/assets/gallery-5.webp";
import gallery6 from "@/assets/gallery-6.webp";
import ProductQuantityControl from "@/components/ProductQuantityControl";
import SEO from "@/components/SEO";
interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  image_url: string;
}
const Index = () => {
  const [homepageProducts, setHomepageProducts] = useState<Product[]>([]);
  const {
    toast
  } = useToast();
  const aboutSection = useScrollAnimation();
  const servicesSection = useScrollAnimation();
  const productsSection = useScrollAnimation();
  const gallerySection = useScrollAnimation();
  const locationSection = useScrollAnimation();
  useEffect(() => {
    fetchHomepageProducts();
  }, []);
  const fetchHomepageProducts = async () => {
    try {
      const {
        data,
        error
      } = await supabase.from("products").select("id, name, description, price, image_url").eq("active", true).eq("show_on_homepage", true).order("created_at", { ascending: true });
      if (error) throw error;
      setHomepageProducts(data || []);
    } catch (error: any) {
      console.error("Error fetching homepage products:", error);
    }
  };
  return <div className="min-h-screen">
      <SEO
        title="Bo Barbers | Barbier & Herenkapper in Utrecht"
        description="Bo Barbers is een unieke barbershop in de binnenstad van Utrecht waar traditie herleeft. Perfectie, precisie en persoonlijke aandacht."
        path="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BarberShop",
          name: "Bo Barbers",
          image: "https://storage.googleapis.com/gpt-engineer-file-uploads/WlWPavVFeoQ4iwj9OE5ssXr0WM72/uploads/1764799660073-Bo Barbers logo zwart.png",
          telephone: "+31307200888",
          email: "info@bobarbers.nl",
          url: "https://bobarbers.nl",
          address: {
            "@type": "PostalAddress",
            streetAddress: "Nobelstraat 263",
            postalCode: "3512 EM",
            addressLocality: "Utrecht",
            addressCountry: "NL",
          },
          openingHoursSpecification: [
            { "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday","Tuesday","Wednesday","Friday"], opens: "08:30", closes: "17:30" },
            { "@type": "OpeningHoursSpecification", dayOfWeek: "Thursday", opens: "08:30", closes: "19:00" },
            { "@type": "OpeningHoursSpecification", dayOfWeek: "Saturday", opens: "09:00", closes: "15:00" },
          ],
          sameAs: ["https://www.instagram.com/bobarbers.nl/"],
        }}
      />
      <Header />
      
      {/* Hero Section */}
      <section className="relative min-h-[60vh] md:min-h-screen flex items-center overflow-hidden pt-20 md:pt-0">
        <div className="absolute inset-0 overflow-hidden">
          <img
            src={heroImage}
            alt="Bo Barbers team aan het werk in de barbershop in Utrecht"
            className="w-full h-full object-cover animate-cinematic-zoom"
            loading="eager"
          />
          <div className="absolute inset-0 bg-black/40 md:bg-gradient-to-r md:from-black/60 md:via-black/40 md:to-transparent" />
        </div>

        <div className="container mx-auto px-4 sm:px-6 relative z-10 py-24 sm:py-28 md:py-32">
          <div className="max-w-7xl mx-auto">
            <div className="text-center md:text-left max-w-xl lg:max-w-2xl mx-auto md:mx-0">
              <p
                className="text-white/70 md:text-white/80 uppercase tracking-[0.2em] sm:tracking-[0.3em] mb-4 sm:mb-6 md:mb-8 text-xs font-semibold animate-fade-in"
                style={{
                  animationDelay: "0.2s",
                  animationFillMode: "forwards",
                }}
              >
                WAAR TRADITIE HERLEEFT
              </p>
              <h1
                style={{
                  animationDelay: "0.4s",
                  animationFillMode: "forwards",
                }}
                className="font-display text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-bold mb-6 sm:mb-8 leading-[0.95] text-white animate-fade-in xl:text-7xl"
              >
                Welkom bij
                <br />
                Bo Barbers
              </h1>
              <div
                className="w-16 sm:w-24 h-1 bg-white mb-8 sm:mb-10 mx-auto md:mx-0 animate-fade-in"
                style={{
                  animationDelay: "0.6s",
                  animationFillMode: "forwards",
                }}
              />
              <p
                className="text-white/90 mb-10 sm:mb-12 text-sm sm:text-base md:text-lg leading-relaxed max-w-xl mx-auto md:mx-0 animate-fade-in"
                style={{
                  animationDelay: "0.8s",
                  animationFillMode: "forwards",
                }}
              >
                Een unieke barbershop in de binnenstad van Utrecht waar jij jezelf als man kunt laten verzorgen door middel van traditionele behandelingen. Perfectie, precisie en persoonlijke aandacht staan bij ons centraal.
              </p>
              <div
                className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center md:justify-start items-center animate-fade-in"
                style={{
                  animationDelay: "1s",
                  animationFillMode: "forwards",
                }}
              >
                <Button
                  asChild
                  size="lg"
                  className="bg-white text-black hover:bg-white/90 rounded-full px-8 sm:px-14 py-6 sm:py-7 text-xs uppercase tracking-[0.15em] sm:tracking-[0.2em] font-bold transition-all hover:scale-105 shadow-2xl w-full sm:w-auto"
                >
                  <a href="/products">Bekijk Producten</a>
                </Button>
                <a
                  href="tel:0307200888"
                  className="hidden md:block text-white text-sm tracking-wider hover:text-white/70 transition-colors font-medium border-b border-white/50 pb-1"
                >
                  030 720 0 888
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce hidden md:block">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2">
            <div className="w-1 h-2 bg-white/50 rounded-full"></div>
          </div>
        </div>
      </section>


      {/* About Section */}
      <section ref={aboutSection.ref} className="py-16 sm:py-24 md:py-32 bg-foreground">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-6xl mx-auto">
            <div className={`text-center mb-12 sm:mb-16 md:mb-20 transition-all duration-1000 ${aboutSection.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <p className="text-white/60 uppercase tracking-[0.2em] sm:tracking-[0.3em] mb-4 sm:mb-6 text-xs font-semibold">
                Over Ons
              </p>
              <h2 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-[1.1] mb-6 sm:mb-8 px-4">
                TRADITIE ONTMOET
                <br />
                MODERN VAKMANSCHAP
              </h2>
              <div className="w-16 sm:w-24 h-1 bg-white mx-auto"></div>
            </div>

            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 md:gap-16 items-center mb-12 sm:mb-16 md:mb-20 transition-all duration-1000 delay-200 ${aboutSection.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <div className="relative overflow-hidden group">
                <img src={aboutTeamImage} alt="Bo Barbers team aan het werk - Barbershop Utrecht" className="w-full aspect-[4/5] object-cover transition-transform duration-700 group-hover:scale-110" />
              </div>

              <div className="space-y-4 sm:space-y-6 md:space-y-8 text-white/80 leading-relaxed text-sm sm:text-base">
                <p className="sm:text-lg">
                  Bo Barbers is een unieke barbershop in de binnenstad van Utrecht waar traditie herleeft. 
                  Een plek waar jij jezelf als man kunt laten verzorgen door middel van traditionele behandelingen. 
                  Bij ons draait het niet alleen om een frisse coupe of strakke baard – het draait om <span className="text-white font-semibold">perfectie, 
                  precisie en persoonlijke aandacht</span>.
                </p>
                <p>
                  Wat ons uniek maakt? Onze barbiers zijn échte vakmensen met jarenlange ervaring, opgedaan in 
                  verschillende barbershops in Europa, samengebracht in één shop. Dankzij onze diverse achtergronden 
                  en hechte samenwerking als team brengen we elke knip- en scheerbeurt naar een hoger niveau.
                </p>
                <p>
                  Bij ons verlaat je de stoel met een uitstraling die past bij jou. No-nonsense, koffie of een biertje 
                  en een goed gesprek. Waar de aandacht voor de klant, kwaliteit en service centraal staat.
                </p>
              </div>
            </div>

            {/* Stats */}
            <div className={`grid grid-cols-3 gap-4 sm:gap-6 md:gap-8 border-t border-b border-white/20 py-8 sm:py-10 md:py-12 transition-all duration-1000 delay-300 ${aboutSection.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <div className="text-center">
                <div className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white mb-1 sm:mb-2">10+</div>
                <p className="text-white/60 uppercase tracking-[0.1em] sm:tracking-[0.2em] text-[10px] sm:text-xs">Jaren Ervaring</p>
              </div>
              <div className="text-center border-l border-r border-white/20">
                <div className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white mb-1 sm:mb-2">3.7K+</div>
                <p className="text-white/60 uppercase tracking-[0.1em] sm:tracking-[0.2em] text-[10px] sm:text-xs">Tevreden Klanten</p>
              </div>
              <div className="text-center">
                <div className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white mb-1 sm:mb-2">200+</div>
                <p className="text-white/60 uppercase tracking-[0.1em] sm:tracking-[0.2em] text-[10px] sm:text-xs">5-Sterren Reviews</p>
              </div>
            </div>

            <div className={`text-center mt-12 sm:mt-14 md:mt-16 transition-all duration-1000 delay-400 ${aboutSection.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <Button asChild size="lg" className="bg-white text-foreground hover:bg-white/90 rounded-full px-8 sm:px-10 md:px-14 py-6 sm:py-7 text-xs uppercase tracking-[0.15em] sm:tracking-[0.2em] font-bold transition-all hover:scale-105 w-full sm:w-auto max-w-xs sm:max-w-none">
                <a href="/about">
                  Meer Over Ons
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section ref={servicesSection.ref} className="py-16 sm:py-24 md:py-32 bg-darkSection">
        <div className="container mx-auto px-4 sm:px-6">
          <div className={`text-center mb-12 sm:mb-16 md:mb-20 transition-all duration-1000 ${servicesSection.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <p className="text-white/60 uppercase tracking-[0.2em] sm:tracking-[0.3em] mb-4 sm:mb-6 text-xs font-semibold">
              Onze Services
            </p>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 text-white leading-[1.2] px-4">
              SUPREME CUTS
              <br />
              CREATIVE FADES
              <br />
              MODERN STYLE
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 max-w-5xl mx-auto">
            {[{
            title: "Herenkapsels",
            desc: "Professionele herenkapsels in Utrecht. Klassieke en moderne stijlen aangepast aan jouw gezichtsvorm en persoonlijkheid",
            price: "Vanaf €29"
          }, {
            title: "Baardverzorging",
            desc: "Traditionele baardtrim en -verzorging met precisie. Van klassieke stijlen tot moderne fades en baardstyling",
            price: "Vanaf €36"
          }, {
            title: "Combinatie",
            desc: "Complete verzorging: knipbeurt én baardverzorging. No-nonsense service met persoonlijke aandacht en een goed gesprek",
            price: "Vanaf €53"
          }].map((service, i) => <Card key={i} className={`p-6 sm:p-8 bg-white border-white/10 hover:shadow-lg transition-all duration-500 hover:-translate-y-2 ${servicesSection.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{
            transitionDelay: servicesSection.isVisible ? `${i * 150}ms` : '0ms'
          }}>
                <h3 className="font-display text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-foreground uppercase">{service.title}</h3>
                <p className="text-foreground/70 leading-relaxed text-sm mb-4 sm:mb-6">{service.desc}</p>
                <div className="text-lg sm:text-xl font-bold text-foreground">{service.price}</div>
              </Card>)}
          </div>

          <div className={`text-center mt-10 sm:mt-12 md:mt-16 transition-all duration-1000 delay-300 ${servicesSection.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <Button asChild size="lg" className="bg-white text-foreground hover:bg-white/90 rounded-full px-8 sm:px-12 md:px-16 py-6 sm:py-7 text-xs uppercase tracking-[0.15em] sm:tracking-[0.2em] font-bold transition-all hover:scale-105 w-full sm:w-auto max-w-xs sm:max-w-none">
              <a href="/services">
                Bekijk Alle Services
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section ref={productsSection.ref} className="py-16 sm:py-24 md:py-32 bg-background">
        <div className="container mx-auto px-4 sm:px-6">
          <div className={`text-center mb-12 sm:mb-16 transition-all duration-1000 ${productsSection.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <p className="text-foreground/60 uppercase tracking-[0.2em] sm:tracking-[0.3em] mb-4 sm:mb-6 text-xs font-semibold">
              Producten
            </p>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold mb-6 sm:mb-8 text-foreground leading-[1.2] px-4">
              PRODUCTEN
              <br />
              DIE HET VERSCHIL MAKEN
            </h2>
            <div className="w-16 sm:w-24 h-1 bg-foreground mx-auto mb-6 sm:mb-8"></div>
            <p className="text-foreground/70 max-w-2xl mx-auto text-sm leading-relaxed px-4">
              Bij Bo Barbers bieden we een zorgvuldig geselecteerd assortiment professionele verzorgingsproducten. 
              Van traditionele baardverzorging tot moderne styling producten - elk product is gekozen om jou te helpen 
              op je best eruit te zien en je grooming routine naar een hoger niveau te tillen.
            </p>
          </div>

          {homepageProducts.length > 0 ? <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8 max-w-5xl mx-auto mb-8 sm:mb-12">
              {homepageProducts.map((product, i) => <Card key={product.id} className={`group overflow-hidden bg-card border-border transition-all duration-700 hover:shadow-2xl hover:-translate-y-2 ${productsSection.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{
            transitionDelay: productsSection.isVisible ? `${i * 150}ms` : '0ms'
          }}>
                  <div className="aspect-square bg-muted relative overflow-hidden">
                    <img src={product.image_url} alt={product.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </div>
                  <div className="p-5 sm:p-6 md:p-8">
                    <h3 className="font-display text-base sm:text-lg font-bold mb-3 sm:mb-4 text-foreground uppercase tracking-wide line-clamp-2 min-h-[2.5rem] sm:min-h-[3rem]">
                      {product.name}
                    </h3>
                    <div className="flex items-center justify-between">
                      <span className="text-xl sm:text-2xl font-bold text-foreground tabular-nums">€{product.price.toFixed(2)}</span>
                      <ProductQuantityControl product={product} />
                    </div>
                  </div>
                </Card>)}
            </div> : <p className="text-center text-foreground/60 mb-8 sm:mb-12">Geen producten geselecteerd voor homepage</p>}

          <div className={`text-center transition-all duration-1000 delay-300 ${productsSection.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <Button asChild variant="outline" size="lg" className="border-foreground text-foreground hover:bg-foreground hover:text-background rounded-full px-8 sm:px-12 py-6 text-xs uppercase tracking-wider font-semibold w-full sm:w-auto max-w-xs sm:max-w-none">
              <a href="/products">
                Bekijk Alle Producten
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section ref={gallerySection.ref} className="py-16 sm:py-24 md:py-32 bg-card">
        <div className="container mx-auto px-4 sm:px-6">
          <div className={`text-center mb-12 sm:mb-16 md:mb-20 transition-all duration-1000 ${gallerySection.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <p className="text-foreground/60 uppercase tracking-[0.2em] sm:tracking-[0.3em] mb-4 sm:mb-6 text-xs font-semibold">
              Portfolio
            </p>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-foreground leading-[1.2] px-4">
              ONZE WERKEN
              <br />
              VAKMANSCHAP IN BEELD
            </h2>
            <p className="text-foreground/60 text-sm mt-4 max-w-2xl mx-auto px-4">
              Ontdek de kwaliteit en precisie van onze barbers. Elke knipbeurt en baardverzorging wordt met zorg en aandacht uitgevoerd.
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 sm:gap-4">
            {[{
            img: gallery1,
            alt: "Professionele herenkapsel bij Bo Barbers Utrecht - Traditionele barbershop"
          }, {
            img: gallery2,
            alt: "Baardverzorging en styling bij Bo Barbers - Vakmanschap in Utrecht"
          }, {
            img: gallery3,
            alt: "Moderne fade en knipbeurt bij Bo Barbers barbershop Utrecht"
          }, {
            img: gallery4,
            alt: "Klassieke herenkapsel bij Bo Barbers - Binnenstad Utrecht"
          }, {
            img: gallery5,
            alt: "Baardtrim en styling service bij Bo Barbers Utrecht"
          }, {
            img: gallery6,
            alt: "Professionele knipbeurt en baardverzorging bij Bo Barbers"
          }].map((item, i) => <div key={i} className={`aspect-square overflow-hidden group transition-all duration-700 ${gallerySection.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{
            transitionDelay: gallerySection.isVisible ? `${i * 100}ms` : '0ms'
          }}>
                <img src={item.img} alt={item.alt} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 cursor-pointer" />
              </div>)}
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="py-16 sm:py-24 md:py-32 bg-card">
        <div className="container mx-auto px-4 sm:px-6">
          <ReviewsCarousel />
        </div>
      </section>

      {/* Location CTA */}
      <section ref={locationSection.ref} className="py-16 sm:py-24 md:py-32 bg-background text-center">
        <div className={`container mx-auto px-4 sm:px-6 transition-all duration-1000 ${locationSection.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <p className="text-foreground/60 uppercase tracking-[0.2em] sm:tracking-[0.3em] mb-4 sm:mb-6 text-xs font-semibold">
            Locatie
          </p>
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold mb-6 sm:mb-8 text-foreground leading-[1.2] px-4">
            BEZOEK ONZE
            <br />
            BARBERSHOP IN UTRECHT
          </h2>
          <p className="text-foreground/70 mb-3 sm:mb-4 text-sm px-4">Nobelstraat 263, 3512 EM Utrecht</p>
          <p className="text-foreground/70 mb-4 sm:mb-6 text-sm px-4">Tel: <a href="tel:0307200888" className="hover:text-foreground transition-colors">030 720 0 888</a> | Email: <a href="mailto:info@bobarbers.nl" className="hover:text-foreground transition-colors">info@bobarbers.nl</a></p>
          <p className="text-foreground/60 text-xs max-w-md mx-auto px-4">
            Maandag t/m Vrijdag: 08:30 - 17:30 (Donderdag tot 19:00) | Zaterdag: 09:00 - 15:00
          </p>
        </div>
      </section>

      <Footer hideInfoSection />
    </div>;
};
export default Index;